import { Children, type PropsWithChildren, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $dashboardNotificationStore, $menuStore, $profileFeatureStore } from '@lib/store';
import { useDocumentTitle, useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import { NotificationsModal } from '@widgets';
import { Button, Footer, Icon, Sidebar, Spinner, Text } from './';
import NotificationIcon from '@assets/icons/notification.svg';
import NotificationActiveIcon from '@assets/icons/notification-active.svg';
import ChevronIcon from '@assets/icons/chevron.svg';
import BurgerIcon from '@assets/icons/dashboard/burger.svg';
import BurgerDarkIcon from '@assets/icons/dashboard/burger-dark.svg';
import GroupIcon from '@assets/icons/group.svg';

type PageContainerProps = {
  className?: string;
  loading?: boolean;
  title?: string;
  back?: boolean;
  backText?: string;
  to?: string | null;
  header?: ReactNode;
  topRightAddons?: ReactNode;
  sidebar?: JSX.Element;
  return?: boolean;
} & PropsWithChildren;

export const PageContainer = (props: PageContainerProps) => {
  const { back, navigate } = useRouteHook();

  const isCollapsedMenu = useUnit($menuStore.store);
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const dashboardNotificationStore = useUnit($dashboardNotificationStore.store);

  const [showNotificationsModal, setShowNotificationsModal] = useState<boolean>(false);
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const sideBarRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  useDocumentTitle(props.title ?? t('title'));

  useEffect(() => {
    if (profileFeatureStore.data?.settings?.viewNotifications) $dashboardNotificationStore.events.getAll();
  }, [profileFeatureStore.data?.settings?.viewNotifications]);

  const isHasAnyUnreadNotifications = useMemo(() => dashboardNotificationStore.data.some((n) => !n.isRead), [dashboardNotificationStore]);
  return (
    <>
      <div className='flex h-screen min-h-screen w-screen flex-row'>
        {PaletteUtils.isVcfcPalette ? (
          <div
            className={cn(
              'max-md:fixed max-md:z-50 max-md:transition-transform max-md:duration-300',
              showMobileMenu ? 'max-md:translate-x-0' : 'max-md:-translate-x-full',
            )}
          >
            <Sidebar ref={sideBarRef} />
          </div>
        ) : (
          <Sidebar ref={sideBarRef} />
        )}
        {PaletteUtils.isVcfcPalette && showMobileMenu && (
          <div className='fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden' onClick={() => setShowMobileMenu((prev) => !prev)} />
        )}
        <div className='flex h-screen min-h-screen flex-1 flex-col overflow-hidden'>
          <div
            className={cn(
              'flex h-[71px] max-h-[71px] min-h-[71px] w-full',
              PaletteUtils.isInsigniaPalette &&
                'items-center justify-between border-b border-b-gray-black bg-white px-6 dark:border-b-gray-wet-asphalt dark:bg-soft-black',
              PaletteUtils.isVcfcPalette && [
                'items-end bg-ghost-white px-10 pb-1 dark:bg-eerie-black max-md:h-[44px] max-md:max-h-[44px] max-md:min-h-[44px] max-md:justify-between max-md:bg-white max-md:px-4 max-md:dark:bg-soft-black',
                props.return && 'items-center bg-white dark:bg-soft-black',
              ],
            )}
          >
            {props.back ? (
              <div className='group inline-flex cursor-pointer items-center gap-1' onClick={() => (props.to ? navigate(props.to) : back())}>
                <ChevronIcon />
                <Text className='group-hover:underline' size='sm'>
                  {props.backText ?? t('layout.header.goBack')}
                </Text>
              </div>
            ) : (
              <>
                {PaletteUtils.isVcfcPalette && (
                  <Button
                    color='primary'
                    className={cn(
                      'dark:bg-bg-soft-black mr-6 h-8 w-8 !p-0 hover:bg-midnight-graphite dark:border dark:border-primary',
                      'max-md:mr-0 max-md:bg-transparent max-md:hover:bg-transparent max-md:focus:bg-transparent md:hidden',
                    )}
                    onClick={() => setShowMobileMenu((prev) => !prev)}
                  >
                    <Icon
                      icon={
                        <>
                          <BurgerIcon className='dark:hidden' />
                          <BurgerDarkIcon className='hidden dark:block' />
                        </>
                      }
                      className='dark:text-primary'
                    />
                  </Button>
                )}
                {isCollapsedMenu && (
                  <Button
                    color='primary'
                    className={cn(
                      'mr-6 h-8 w-8 rounded-sm !p-0',
                      PaletteUtils.isVcfcPalette && [
                        '!rounded-3xl hover:bg-midnight-graphite dark:border dark:border-primary dark:bg-white hover:dark:bg-obsidian-shade',
                        'max-md:hidden',
                      ],
                    )}
                    onClick={() => $menuStore.events.setIsCollapsed(!isCollapsedMenu)}
                  >
                    <Icon
                      className='rotate-180'
                      color={PaletteUtils.getColor('secondary', 'white')}
                      icon={<ChevronIcon className={cn(PaletteUtils.isVcfcPalette && 'dark:text-primary')} />}
                    />
                  </Button>
                )}
                <Text weight={PaletteUtils.isVcfcPalette ? 'medium' : 'semibold'} size={PaletteUtils.isVcfcPalette ? '3xl' : '2xl'}>
                  {props.header}
                </Text>
                <div className={cn('flex items-center', PaletteUtils.isVcfcPalette && ['ml-auto', 'max-md:ml-0'])}>
                  {PaletteUtils.isVcfcPalette && (
                    <div className={cn('relative', 'md:hidden')}>
                      <Button onClick={() => setMobileMenuOpen((prev) => !prev)}>
                        <Icon icon={<GroupIcon className='dark:text-white' />} />
                      </Button>
                      {mobileMenuOpen && (
                        <div className='absolute right-0 top-full z-50 rounded-bl-md rounded-br-md bg-white py-2 dark:bg-soft-black'>
                          {profileFeatureStore.data?.settings.viewNotifications && (
                            <Button
                              className='hover:bg-gray-100 dark:hover:bg-gray-700 flex w-full items-center px-4 py-2 text-left'
                              onClick={() => {
                                setShowNotificationsModal(true);
                                setMobileMenuOpen(false);
                              }}
                            >
                              <Icon icon={isHasAnyUnreadNotifications ? <NotificationActiveIcon /> : <NotificationIcon />} />
                            </Button>
                          )}
                          {props.topRightAddons &&
                            Children.map(props.topRightAddons, (child, idx) => (
                              <div key={idx} className='hover:bg-gray-100 dark:hover:bg-gray-700 px-4 py-2' onClick={() => setMobileMenuOpen(false)}>
                                {child}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className={cn('hidden items-center', 'md:flex')}>
                    {profileFeatureStore.data?.settings?.viewNotifications && (
                      <Button
                        className={cn(
                          'mx-5 h-10 w-10 rounded-sm',
                          PaletteUtils.isVcfcPalette && 'bg-light/10',
                          'hover:bg-light/60 dark:hover:bg-light/30',
                        )}
                        onClick={() => setShowNotificationsModal(true)}
                      >
                        <Icon icon={isHasAnyUnreadNotifications ? <NotificationActiveIcon /> : <NotificationIcon />} />
                      </Button>
                    )}
                    {props.topRightAddons && <div className='flex'>{props.topRightAddons}</div>}
                  </div>
                </div>
              </>
            )}
          </div>
          <div
            className={cn(
              'flex h-[calc(100vh-71px)] w-full flex-col overflow-y-auto max-md:h-full',
              PaletteUtils.isVcfcPalette && 'bg-ghost-white pt-6 dark:bg-eerie-black',
            )}
          >
            <main
              className={cn(
                'w-full',
                PaletteUtils.isInsigniaPalette && 'p-8',
                PaletteUtils.isVcfcPalette && ['px-10 pb-4', 'max-md:px-4'],
                props.className,
              )}
            >
              {props.loading ? <Spinner /> : props.children}
            </main>
            <Footer />
          </div>
        </div>
      </div>
      <NotificationsModal isOpen={showNotificationsModal} onClose={() => setShowNotificationsModal(false)} />
    </>
  );
};
